import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';

import { Save, X } from "lucide-react";

import { Modal } from "./Index";
import { TextInput } from "../shared/Inputs";
import { areEntitlementsEqual, isEntitlementsInvalid, ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { closeModal, getModalData, isModalActive } from "../../store/modal.slice";

import { useGetSubClient, useSaveSubClient } from "../../hooks/sub_clients-cax";
import { useGetEntitlementsForAdminForm } from "../../hooks/entitlements";

import { IEntitlementsData } from "../../types/misc.interface";
import { EEntitlementSource, EProduct } from "../../types/misc.enum";

const MODAL_NAME = "MANAGE_SUB_CLIENT_MODAL";

export const ManageSubClientModal = () => {
    const dispatch = useDispatch();

    const isModalOpen = useSelector(isModalActive(MODAL_NAME));
    const modalData = useSelector(getModalData(MODAL_NAME));

    const [name, setName] = useState<string>("");
    const [currentEntitlements, setCurrentEntitlements] = useState<IEntitlementsData>({
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { data: subClientDetails } = useGetSubClient(modalData?.subClientId ?? null);
    const { mutate: saveSubClient } = useSaveSubClient();
    const { data: availableEntitlements } = useGetEntitlementsForAdminForm(subClientDetails?.id ? subClientDetails.id.toString() : null, EEntitlementSource.SUB_CLIENT);

    const onCloseModal = () => {
        dispatch(closeModal(MODAL_NAME));
    };

    const resetForm = () => {
        setName("");
        setCurrentEntitlements({
            products: [],
            mbts_modules: [],
            mcsa_modules: [],
            mbts_assessments: 0,
            mcsa_assessments: 0,
        });
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    useEffect(() => {
        if (subClientDetails) {
            setName(subClientDetails.name ?? "");
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subClientDetails]);

    const isDataSame = () => {
        const isEntitlementsSame = areEntitlementsEqual(currentEntitlements, subClientDetails?.entitlements);

        if (!!subClientDetails?.id) {
            return (
                name === subClientDetails?.name
                && isEntitlementsSame
            );
        };

        return name === subClientDetails?.name && isEntitlementsSame;
    };

    const isFormFilled = () => {
        return name.trim() !== "";
    };

    const handleSave = () => {
        if (isProcessing || !isFormFilled() || isDataSame() || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)) {
            return;
        };

        const products = currentEntitlements.products.map(product => product.key);
        const mbtsModules = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_modules.map(module => module.id) : [];
        const mcsaModules = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_modules.map(module => module.id) : [];
        const mbtsAssessments = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_assessments : 0;
        const mcsaAssessments = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_assessments : 0;

        saveSubClient({
            payload: {
                id: subClientDetails?.id ?? null,
                name,
                entitlements: {
                    products: products,
                    mbts_modules: mbtsModules,
                    mcsa_modules: mcsaModules,
                    mbts_assessments: mbtsAssessments,
                    mcsa_assessments: mcsaAssessments,
                },
            },
            setIsProcessing,
        });
    };

    return (
        <Modal
            name={MODAL_NAME}
            title="Manage Sub Client"
            baseClass="manage_sub_client"
            topupLvl={0}
            onClose={onCloseModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
        >
            <button className="modal__close_btn" onClick={onCloseModal}><X /></button>
            <p className="color--primary text-xl text-bold mb-2xl">{subClientDetails?.id ? 'Update' : 'Add'} sub client</p>
            <div className="modal__sub_client_form">
                <div className="modal__sub_client_form__block">
                    <div className="modal__sub_client_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Name</p>
                    </div>
                    <div className="modal__sub_client_form__block__right">
                        <div className="d-flex gap-xl">
                            <TextInput
                                placeholder="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                {!!subClientDetails?.id && !!availableEntitlements?.maximum_caps?.products?.length && (
                    <ModalEntitlementForm
                        isModalOpen={isModalOpen}
                        source={EEntitlementSource.SUB_CLIENT}
                        availableEntitlements={availableEntitlements}
                        entitlements={subClientDetails?.entitlements ?? null}
                        onEntitlementsChange={setCurrentEntitlements}
                        isProcessing={isProcessing}
                        assessmentsTooltip="Maximum number of {PRODUCT_NAME} assessments allowed to be distributed between beneficiaries (Cohorts, Users)."
                    />
                )}
                <div className="modal__sub_client_form__actions mt-xl">
                    <button
                        className="btn btn--sm btn--radius-sm btn--primary"
                        onClick={handleSave}
                        disabled={!isFormFilled() || isDataSame() || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)}
                    >
                        <Save />
                        <span>{isProcessing ? 'Saving...' : 'Save'}</span>
                        {isProcessing && <span className="btn-loader" />}
                    </button>
                </div>
            </div>
        </Modal>
    );
};